import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const ROLES = [
  { value: 'admin', label: '⭐ Admin', desc: 'Full access to all features' },
  { value: 'staff', label: '👤 Staff', desc: 'Scan QR & view tickets only' }
];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ username: '', password: '', displayName: '', role: 'staff' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const load = () => api.get('/admin/users').then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password.trim()) {
      return setMsg('Username and password are required');
    }
    setSaving(true);
    setMsg('');
    try {
      await api.post('/admin/users', form);
      setForm({ username: '', password: '', displayName: '', role: 'staff' });
      setShowForm(false);
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    await api.put(`/admin/users/${user._id}`, { isActive: !user.isActive });
    load();
  };

  const handleDelete = async (id, username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  const startEdit = (user) => {
    setEditId(user._id);
    setEditData({ displayName: user.displayName, role: user.role, password: '' });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/admin/users/${id}`, editData);
      setEditId(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const fmt = (d) => new Date(d).toLocaleDateString();

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Users</h1>
        <p className="admin-page-sub">{users.length} total · Manage admin and staff accounts</p>
      </div>

      {/* Role legend */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        {ROLES.map(r => (
          <div key={r.value} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 18px', flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: r.value === 'admin' ? '#d4af37' : '#60a5fa', marginBottom: 4 }}>{r.label}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{r.desc}</div>
          </div>
        ))}
      </div>

      {/* Add user button */}
      <div className="admin-toolbar">
        <button className="admin-btn" onClick={() => { setShowForm(s => !s); setMsg(''); }}>
          {showForm ? '✕ Cancel' : '+ Add User'}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="admin-card">
          <div className="admin-card-title">New User</div>
          {msg && <p style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{msg}</p>}
          <form onSubmit={handleCreate}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="admin-field" style={{ marginBottom: 0 }}>
                <label className="admin-label">Username *</label>
                <input className="admin-input" placeholder="e.g. sokha.chan" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
              </div>
              <div className="admin-field" style={{ marginBottom: 0 }}>
                <label className="admin-label">Display Name</label>
                <input className="admin-input" placeholder="e.g. Sokha Chan" value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} />
              </div>
              <div className="admin-field" style={{ marginBottom: 0 }}>
                <label className="admin-label">Password *</label>
                <input className="admin-input" type="password" placeholder="Set password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div className="admin-field" style={{ marginBottom: 0 }}>
                <label className="admin-label">Role</label>
                <select className="admin-select" style={{ width: '100%', padding: '12px 14px' }} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="staff">👤 Staff — Scan & Tickets only</option>
                  <option value="admin">⭐ Admin — Full access</option>
                </select>
              </div>
            </div>
            <button className="admin-btn" type="submit" disabled={saving} style={{ marginTop: 16, padding: '11px 28px' }}>
              {saving ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      {/* Users table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Username</th>
              <th>Role</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'rgba(255,255,255,0.3)' }}>No users yet</td></tr>
            ) : users.map(u => (
              <tr key={u._id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: u.role === 'admin' ? 'linear-gradient(135deg,#d4af37,#f0c55a)' : 'linear-gradient(135deg,#1e40af,#3b82f6)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                      {(u.displayName || u.username || '?')[0].toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 600, color: '#fff' }}>{u.displayName || '—'}</span>
                  </div>
                </td>
                <td style={{ fontFamily: 'monospace', color: '#d4af37' }}>{u.username}</td>
                <td>
                  {editId === u._id ? (
                    <select className="admin-select" style={{ padding: '4px 8px', fontSize: 12 }} value={editData.role} onChange={e => setEditData(d => ({ ...d, role: e.target.value }))}>
                      <option value="staff">👤 Staff</option>
                      <option value="admin">⭐ Admin</option>
                    </select>
                  ) : (
                    <span className={`badge ${u.role === 'admin' ? 'approved' : 'pending'}`}>
                      {u.role === 'admin' ? '⭐ Admin' : '👤 Staff'}
                    </span>
                  )}
                </td>
                <td>
                  <span className={`badge ${u.isActive ? 'approved' : 'declined'}`}>
                    {u.isActive ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td>{fmt(u.createdAt)}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {editId === u._id ? (
                      <>
                        <div style={{ marginBottom: 4 }}>
                          <input
                            className="admin-input" type="password" placeholder="New password (optional)"
                            value={editData.password} onChange={e => setEditData(d => ({ ...d, password: e.target.value }))}
                            style={{ padding: '5px 10px', fontSize: 12, marginBottom: 4 }}
                          />
                        </div>
                        <button className="admin-btn" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => saveEdit(u._id)}>Save</button>
                        <button className="admin-btn admin-btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => setEditId(null)}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button className="admin-btn admin-btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => startEdit(u)}>Edit</button>
                        <button className="admin-btn admin-btn-ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => handleToggleActive(u)}>
                          {u.isActive ? 'Disable' : 'Enable'}
                        </button>
                        {u.username !== 'admin' && (
                          <button className="admin-btn admin-btn-danger" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => handleDelete(u._id, u.username)}>Delete</button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
